import {
  track,
  flushTrack,
  type FlushOptions,
} from "@tamara/web-frontend-library/tracking";

import { RIVER_ARM, type RiverEventName } from "./event-names";

/** Fixed-precision Money for River: value = unscaled_value / 10^scale. */
export interface RiverMoney {
  currency: string;
  amount: { unscaled_value: string; scale: number };
}

/**
 * Major units (e.g. 250.5 SAR) -> Money with scale 2.
 *
 * Scale is fixed at 2 to match prequal-ui. River does NOT validate that scale
 * suits the currency, and a mismatch silently misreports the amount in
 * BigQuery by a factor of 100. KWD/BHD/OMR are 3-decimal currencies — if we
 * ever report on them at finer than 2dp this needs to derive from `currency`.
 */
const MONEY_SCALE = 2;

export function toRiverMoney(major: number, currency: string): RiverMoney {
  // A non-finite amount would stringify to "NaN" / "Infinity", which is not a
  // valid int64 on the wire: River rejects the publish with 400 and WFL
  // swallows it, so the event would vanish with no diagnostic. Fall back to 0.
  const safe = Number.isFinite(major) ? major : 0;
  return {
    currency: currency.toUpperCase(),
    amount: {
      unscaled_value: String(Math.round(safe * 10 ** MONEY_SCALE)),
      scale: MONEY_SCALE,
    },
  };
}

/** Coarse device class. Data asked for this — it is unreliable from Jerk today. */
function deviceType(win: Window = window): "mobile" | "tablet" | "desktop" {
  const ua = win.navigator?.userAgent ?? "";
  if (/iPad|Tablet|(Android(?!.*Mobile))/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
}

/** True inside the Tamara app WebView (the native bridge is injected there). */
function isWebView(win: Window = window): boolean {
  return (win as { Mobile?: unknown }).Mobile != null;
}

export interface RiverContext {
  locale?: string;
  country?: string;
  merchantId?: string;
  orderId?: string;
  customerId?: string;
  appVersion?: string;
  /** Extra key/values to stamp as-is. Canonical fields win a name collision. */
  extra?: Record<string, string>;
}

/**
 * Shared context stamped on EventMetadata.additional_metadata.
 *
 * String values only — it is a map<string,string> on the wire. Empty values are
 * dropped so we never ship noise. `customer_id` is included deliberately: it is
 * not PII by our definition (confirmed by Philipp), so it rides here rather
 * than needing its own tagged payload field.
 *
 * The raw user agent is deliberately NOT sent. River requires metadata to be
 * PII-free and a map<string,string> cannot carry a policy tag, so a full UA
 * string has nowhere safe to live. `device_type` covers the ask.
 */
export function riverMetadata(
  ctx: RiverContext = {},
  win: Window = window,
): Record<string, string> {
  const meta: Record<string, string> = {};

  // Passthrough first, so the canonical fields below always overwrite a key of
  // the same name rather than being silently replaced by caller input.
  for (const [key, value] of Object.entries(ctx.extra ?? {})) {
    if (value != null && value !== "") meta[key] = String(value);
  }

  meta.device_type = deviceType(win);
  meta.is_webview = String(isWebView(win));

  const optional: Record<string, string | undefined> = {
    locale: ctx.locale,
    country: ctx.country,
    merchant_id: ctx.merchantId,
    order_id: ctx.orderId,
    customer_id: ctx.customerId,
    app_version:
      ctx.appVersion ??
      (win as { __CHECKOUT_WEBAPP_VERSION__?: string })
        .__CHECKOUT_WEBAPP_VERSION__,
  };

  for (const [key, value] of Object.entries(optional)) {
    if (value != null && value !== "") meta[key] = value;
  }

  return meta;
}

interface TrackRiverInput {
  /** Event name WITHOUT the CheckoutUI proto prefix. */
  event: RiverEventName;
  /** Kafka key + metadata.entity_id. Always the checkout id for this domain. */
  checkoutId: string;
  /** Body for the `oneof data` arm. Omit for marker events. */
  payload?: Record<string, unknown>;
  context?: RiverContext;
}

/**
 * Publishes one River event.
 *
 * Best-effort by design: WFL no-ops when there is no backend session (local
 * dev) and swallows transport errors, so a failed beacon can never strand the
 * customer. Wrapped anyway so a malformed payload cannot throw into a render.
 */
export function trackRiver({
  event,
  checkoutId,
  payload,
  context,
}: TrackRiverInput): void {
  try {
    track({
      eventName: event,
      entityId: checkoutId,
      additionalMetadata: riverMetadata(context),
      // Marker events carry `occurred: true` so BigQuery can materialise a
      // non-empty RECORD — an empty struct is rejected by the BQ sink.
      payload: { [RIVER_ARM[event]]: payload ?? { occurred: true } },
    });
  } catch {
    // never break the flow for telemetry
  }
}

/**
 * Flush queued events before a hard navigation.
 *
 * The batcher flushes on size, timer and tab-hide, but a full-page redirect
 * (3DS, KYC, auth, merchant return) can drop whatever is still buffered.
 *
 * Pass `{ keepalive: true }` for those redirects, otherwise the browser is
 * free to cancel the in-flight request as the page unloads. Note that
 * keepalive flushes are never retried, so treat them as one shot.
 */
export function flushRiver(options?: FlushOptions): void {
  try {
    flushTrack(options);
  } catch {
    // never break the flow for telemetry
  }
}
